import { useState } from 'react';
import { NavLink } from 'react-router-dom';

import { useUnreadAlertCount } from 'src/api/queries';
import { BrandLogo } from 'src/components/ui/BrandLogo';
import { Icon, type IconName } from 'src/components/ui/Icon';
import { MachineState } from 'src/components/shell/MachineState';
import { useSessionEditor } from 'src/components/editorial/SignInGate';
import { ThemeToggle } from 'src/components/ui/ThemeToggle';
import { readOrStartAlertsSeenAt } from 'src/lib/alertsSeen';
import { endSession } from 'src/lib/session';

/**
 * The rail is a department index, not a sidebar.
 *
 * Set the way a masthead sets its departments: hairline rules, letterspaced heads at caption size,
 * items in the interface face. It carried no icons until 2026-09-20 — the original reasoning
 * (Art Direction §12: a column of outline glyphs beside eight words is decoration) stands as a
 * design opinion, and is recorded here rather than deleted, but the newsroom asked for the
 * department-by-icon convention their design system uses, and that request overrides it. The
 * `Icon` component this draws from is hand-drawn to Design System §3.2's own spec (24-grid,
 * stroke 2, `currentColor`) rather than an adopted icon font, which is the compromise: department
 * glyphs, never the brand mark itself.
 *
 * The two groups are still the argument. **Revisión** is the journalism; **Sistema** is the
 * machine that produces it and the people allowed near it.
 */
type Section = { to: string; label: string; icon: IconName; end?: boolean; badge?: number };

const REVIEW: Array<Section> = [
  { to: '/', label: 'Transmisiones', icon: 'broadcasts', end: true },
  // Neither of the next two is scoped to a broadcast. Both belong in Revisión rather than Sistema
  // because they are journalism — what the newsroom has been covering, and what it asked to be
  // told about — not machinery.
  { to: '/tendencias', label: 'Tendencias', icon: 'trends' },
  // Also not scoped to a broadcast, and also journalism rather than machinery: the standing
  // questions an editor left armed, and everything that has answered them.
  { to: '/alertas', label: 'Alertas', icon: 'alerts' },
  // Read rather than heard, and journalism rather than machinery — same argument as Tendencias.
  { to: '/publicaciones', label: 'Publicaciones', icon: 'posts' },
  { to: '/procesar', label: 'Procesamiento', icon: 'processing' },
];

const SYSTEM: Array<Section> = [
  { to: '/voces', label: 'Voces', icon: 'voices' },
  { to: '/origenes', label: 'Orígenes', icon: 'channels' },
  { to: '/suscripciones', label: 'Suscripciones', icon: 'feeds' },
  { to: '/personas', label: 'Personas', icon: 'people' },
  // What the models cost. Sistema, not Revisión: it is a reading of the machine, not of the journalism.
  { to: '/costos', label: 'Costos', icon: 'costs' },
  { to: '/flujo', label: 'Cómo funciona', icon: 'flow' },
];

export function NavRail({ canAdminister }: { canAdminister: boolean }) {
  const editor = useSessionEditor();
  const system = SYSTEM.filter(section => section.to !== '/personas' || canAdminister);
  const unreadAlerts = useUnreadAlerts();

  const review = REVIEW.map(section => (section.to === '/alertas' && unreadAlerts > 0 ? { ...section, badge: unreadAlerts } : section));

  return (
    <nav className="rail" aria-label="Secciones">
      {/* 2026-09-21 master (public/brand/horizontal-on-navy.svg) carries the tagline itself now —
          no separate type beside it to keep in sync. */}
      <div className="rail__masthead">
        <BrandLogo variant="horizontal" className="rail__logo" />
      </div>

      <MachineState />

      <RailGroup heading="Revisión" sections={review} />
      <RailGroup heading="Sistema" sections={system} />

      <div className="rail__foot">
        {editor && (
          <div className="rail__who">
            {/* Real initials from the signed-in account, not a placeholder tenant name — this
                product has no workspace/org switcher to draw one from. */}
            <span className="rail__avatar" aria-hidden="true">
              {initials(editor.name || editor.email)}
            </span>
            <div className="rail__whoText">
              <span className="rail__whoName" title={editor.email}>
                {editor.name || editor.email}
              </span>
              {/* Real, from the same role check that decides whether Personas is drawn at all —
                  not the mock's flat "Administrador" for everyone. */}
              <span className="rail__whoRole">{canAdminister ? 'Administrador' : 'Editor'}</span>
            </div>
          </div>
        )}
        <div className="rail__footActions">
          <ThemeToggle />
          <button type="button" className="rail__signout" onClick={endSession}>
            Salir
          </button>
        </div>
      </div>
    </nav>
  );
}

/** First letter of the first two words, or the first two characters of a bare email. */
function initials(value: string): string {
  const words = value.trim().split(/\s+/).filter(Boolean);
  if (words.length >= 2) return (words[0]!.charAt(0) + words[1]!.charAt(0)).toUpperCase();
  return value.trim().slice(0, 2).toUpperCase();
}

/**
 * How many alerts have been raised since this browser last opened the history.
 *
 * A count from the server, not a page filtered here. `seenAt` is read once per mount rather than
 * per render, because it is the query key: re-reading it during a render that the query itself
 * triggers is how a badge turns into a refetch loop.
 */
function useUnreadAlerts(): number {
  const [seenAt] = useState(() => readOrStartAlertsSeenAt());
  const { data } = useUnreadAlertCount(new Date(seenAt).toISOString());

  return data?.totalRecords ?? 0;
}

function RailGroup({ heading, sections }: { heading: string; sections: Array<Section> }) {
  return (
    <div className="rail__group">
      <p className="rail__heading">{heading}</p>
      <ul className="rail__list">
        {sections.map(section => (
          <li key={section.to}>
            <NavLink to={section.to} end={section.end} className={({ isActive }) => `rail__item${isActive ? ' rail__item--active' : ''}`}>
              <Icon name={section.icon} />
              {section.label}
              {section.badge !== undefined && (
                <span className="rail__badge" aria-label={`${section.badge} alertas nuevas`}>
                  {section.badge}
                </span>
              )}
            </NavLink>
          </li>
        ))}
      </ul>
    </div>
  );
}
